import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
  enableBodyScroll,
  disableBodyScroll,
  createRef,
  ref,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    overlay: {
      type: Boolean,
    },
    open: {
      reflect: true,
    },
    scrollLock: {
      type: Boolean,
      attribute: 'scroll-lock',
    },
    ignoreTouchScrollLock: {
      type: Boolean,
      attribute: 'ignore-touch-scroll-lock',
    },
    noCloseButton: {
      type: Boolean,
      attribute: 'no-close-button',
    },
    groupModalClass: {
      type: String,
      attribute: 'group-modal-class'
    }
  }

  $scrollableContainer = createRef()

  constructor() {
    super()

    this.overlay = false
    this.scrollLock = false
    this.groupModalClass = ''
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      if (this.open) {
        this.close();
      } else {
        this.open = true;
        if (this.classList.contains("header__megamenu") && !document.body.classList.contains("megamenu-opened")) {
          document.body.classList.add("megamenu-opened");
        }
      }
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }

    if (this.id === data.id && this.groupModalClass) {
      const groupModals = this.parentElement.querySelectorAll(`.${this.groupModalClass}`)
      if (groupModals) {
        groupModals.forEach(element => {
          if (element.getAttribute('open')) {
            element.removeAttribute('open')
          }
        })
      }
    }
  }

  closeFromInside(e) {
    if (
      e.composedPath().some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.close(e)
    }
  }

  close(event) {
    this.dispatchEvent(
      new CustomEvent('modal-close', {
        bubbles: true,
        composed: true,
      })
    )

    this.open = ""
    document.body.classList.remove("megamenu-opened");
		document.querySelectorAll('[js-parent-link]').forEach((link) => {
			link.classList.remove('active');
		})
  }

  closeOnEsc(event) {
    if (event.keyCode != 27 ) return;
    this.close(event);
  }


  toggleScrollLock() {
    if (!this.scrollLock || !this.$scrollableContainer.value) return

    if (this.open) {
			disableBodyScroll(this.$scrollableContainer.value, {
				allowTouchMove: this.ignoreTouchScrollLock,
			})
    } else {
    	enableBodyScroll(this.$scrollableContainer.value)
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      this.toggleScrollLock(); 
    }
  }

  getCloseButton() {
    if (this.noCloseButton) return ''

    return html`<button
      @click="${this.close}"
      class="button"
      type="button"
      aria-label="${t('accessibility.close')}"
    >
      <slot name="close"
        ><span class="close-icon">${unsafeHTML(paths.close)}</span></slot
      >
    </button>`
  }

  render() {
    return html`<div
        id="modal-container"
        @keydown="${this.closeOnEsc}"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        ${this.getCloseButton()}

        <div
          class="content"
          role="document"
          aria-label="${t('products.modal.label')}"
          tabindex="0"
          ${ref(this.$scrollableContainer)}
        >
          <hh-focus-trap active="${this.open}">
            <slot @click="${this.closeFromInside}"></slot>
          </hh-focus-trap>
        </div>
      </div>

      ${(this.overlay &&
        html`<div @click="${this.close}" id="overlay"></div>`) ||
      ''} `
  }

  static styles = css`
    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    :host {
      opacity: 0;
      visibility: hidden;
      z-index: -1;
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      pointer-events: none;
    }

    #modal-container {
      position: fixed;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-color: var(--hh-background);
      z-index: 2;
      pointer-events: auto;
    }

    #overlay {
      position: fixed;
      inset: 0;
      background-color: var(--hh-overlay-color, #000);
      opacity: var(--hh-overlay-opacity, 0.3);
      z-index: 1;
      pointer-events: auto;
    }

    :host([size='flyout']) #modal-container {
      max-width: var(--hh-max-width, 400px);
      height: var(--hh-flyout-height, auto);
      max-height: var(--hh-flyout-max-height);
      top: var(--hh-flyout-offset-top, var(--hh-flyout-offset, 30px));
      right: var(--hh-flyout-offset-right, var(--hh-flyout-offset, 30px));
      bottom: var(--hh-flyout-offset-bottom, auto);
      left: var(--hh-flyout-offset-left, auto);
      transform: var(--hh-flyout-transform, none);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([size='header']) #modal-container {
    	width: 100%;
      height: auto;
			top: calc(var(--hh-header-offset) + 2.5rem);
      right: var(--hh-flyout-offset-right, var(--hh-flyout-offset, 30px));
      left: var(--hh-flyout-offset-left, auto);
      transform: var(--hh-flyout-transform, none);
      left: revert;
    }

    :host([size='header']) #overlay {
      position: fixed;
      inset: 0;
      background-color: var(--hh-overlay-color, #000);
      opacity: var(--hh-overlay-opacity, 0.3);
      z-index: 1;
      pointer-events: auto;
      top: calc(var(--hh-header-offset) + 2.5rem);
    }

    @media screen and (min-width: 750px) {
      :host([size='flyout']) #modal-container {
        right: var(--hh-flyout-offset-right, var(--hh-flyout-offset, 30px));
      }
    }

    :host([size='video']) #modal-container {
      background-color: rgba(0, 0, 0, 0.25);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .button {
      position: fixed;
      top: 4rem;
      right: 4rem;
      color: var(--hh-color);
      cursor: pointer;
      z-index: 3;
      background: none;
      border: 0;
      padding: 0;
    }

    .button .close-icon {
      width: 4rem;
      background-color: var(--hh-background);
      border: 0.1rem solid var(--hh-border-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.2rem;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    :host([size='video']) .button {
      background-color: var(--hh-color-day);
      border-radius: 50%;
      top: 17rem;
      right: 2rem;
    }

    @media screen and (min-width: 750px) {
      :host([size='video']) .button {
        top: 4rem;
        right: 4rem;
      }
    }

    :host([size='video']) .button svg {
      width: 100%;
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow-y: auto;
      overflow-x: hidden;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='flyout']) .content {
      height: 100%;
    }

    :host([size='header']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
